
import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';
const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';



/**
 * Secure API Keys Service
 * Uses backend API endpoints instead of direct Firestore access
 */

export interface ApiKey {
  id?: string;
  name: string;
  description: string;
  organizationId: string;
  scopes: string[];
  createdAt?: any;
  lastUsed?: any;
  expiresAt?: any;
  isActive: boolean;
  environment: 'development' | 'staging' | 'production';
  ipWhitelist?: string[];
  rateLimit: {
    requestsPerMinute: number;
    requestsPerHour: number;
  };
  keyValue?: string; // The actual API key value (only returned on creation)
}

class ApiKeysService {
  async getAllApiKeys(): Promise<ApiKey[]> {
    try {
      console.log('Getting all API keys via API');
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.apiKeys;
    } catch (error) {
      console.error('Error fetching API keys:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async getApiKey(id: string): Promise<ApiKey | null> {
    try {
      console.log('Getting API key via API:', id);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.apiKey;
    } catch (error) {
      console.error('Error fetching API key:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async createApiKey(data: Omit<ApiKey, 'id' | 'createdAt'>): Promise<ApiKey> {
    try {
      console.log('Creating API key via API:', data.name);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/api-keys`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        id: response.data.id,
        ...response.data
      };
    } catch (error) {
      console.error('Error creating API key:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async updateApiKey(id: string, data: Partial<ApiKey>): Promise<void> {
    try {
      console.log('Updating API key via API:', id);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/billing/api-keys/${id}`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error updating API key:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async deleteApiKey(id: string): Promise<void> {
    try {
      console.log('Deleting API key via API:', id);
      const token = await getAuthToken();
      
      await axios.delete(
        `${API_BASE}/billing/api-keys/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error deleting API key:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async toggleApiKeyStatus(id: string): Promise<void> {
    try {
      console.log('Toggling API key status via API:', id);
      
      const apiKey = await this.getApiKey(id);
      if (!apiKey) {
        throw new Error('API key not found');
      }

      await this.updateApiKey(id, {
        isActive: !apiKey.isActive
      });
    } catch (error) {
      console.error('Error toggling API key status:', error);
      throw error;
    }
  }

  async recordApiKeyUsage(id: string): Promise<void> {
    try {
      console.log('Recording API key usage via API:', id);
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/billing/api-keys/${id}/usage`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Error recording API key usage:', error);
      // Don't throw on usage recording errors to avoid breaking API calls
    }
  }

  getAvailableScopes(): string[] {
    return [
      'patients:read',
      'patients:write',
      'appointments:read',
      'appointments:write',
      'billing:read',
      'billing:write',
      'records:read',
      'records:write',
      'admin'
    ];
  }

  validateApiKey(data: Partial<ApiKey>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.name?.trim()) {
      errors.push('Name is required');
    }

    if (!data.description?.trim()) {
      errors.push('Description is required');
    }

    if (!data.organizationId?.trim()) {
      errors.push('Organization ID is required');
    }

    if (!data.scopes?.length) {
      errors.push('At least one scope is required');
    }

    if (data.ipWhitelist?.length) {
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      const invalidIps = data.ipWhitelist.filter(ip => !ipRegex.test(ip));
      if (invalidIps.length) {
        errors.push(`Invalid IP addresses: ${invalidIps.join(', ')}`);
      }
    }

    if (data.rateLimit) {
      if (data.rateLimit.requestsPerMinute < 1) {
        errors.push('Requests per minute must be at least 1');
      }
      if (data.rateLimit.requestsPerHour < 1) {
        errors.push('Requests per hour must be at least 1');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get API key statistics
   */
  async getApiKeyStats(id: string): Promise<any> {
    try {
      console.log('Getting API key statistics via API:', id);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/${id}/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.stats;
    } catch (error) {
      console.error('Error getting API key stats:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Regenerate API key
   */
  async regenerateApiKey(id: string): Promise<{ keyValue: string }> {
    try {
      console.log('Regenerating API key via API:', id);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/api-keys/${id}/regenerate`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error regenerating API key:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Get API key usage history
   */
  async getApiKeyUsageHistory(id: string, dateRange?: { startDate: string; endDate: string }): Promise<any[]> {
    try {
      console.log('Getting API key usage history via API:', id);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (dateRange?.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append('endDate', dateRange.endDate);
      }
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/${id}/usage-history?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.usageHistory;
    } catch (error) {
      console.error('Error getting API key usage history:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Test API key
   */
  async testApiKey(id: string): Promise<{ success: boolean; message: string; responseTime?: number }> {
    try {
      console.log('Testing API key via API:', id);
      const token = await getAuthToken();
      
      const startTime = Date.now();
      
      const response = await axios.post(
        `${API_BASE}/billing/api-keys/${id}/test`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const responseTime = Date.now() - startTime;
      
      return {
        success: response.data.success,
        message: response.data.message,
        responseTime
      };
    } catch (error) {
      console.error('Error testing API key:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        return {
          success: false,
          message: error.response.data.error
        };
      }
      return {
        success: false,
        message: 'Failed to test API key'
      };
    }
  }

  /**
   * Search API keys
   */
  async searchApiKeys(searchTerm: string, filters: any = {}): Promise<ApiKey[]> {
    try {
      console.log('Searching API keys via API:', searchTerm);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.apiKeys;
    } catch (error) {
      // If search endpoint doesn't exist, fallback to getting all and filtering
      try {
        const allApiKeys = await this.getAllApiKeys();
        
        if (!searchTerm || searchTerm.trim() === '') {
          return allApiKeys;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return allApiKeys.filter(apiKey => 
          (apiKey.name && apiKey.name.toLowerCase().includes(lowerSearchTerm)) ||
          (apiKey.description && apiKey.description.toLowerCase().includes(lowerSearchTerm)) ||
          (apiKey.organizationId && apiKey.organizationId.toLowerCase().includes(lowerSearchTerm))
        );
      } catch (fallbackError) {
        console.error('Error searching API keys (fallback):', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Get API keys by organization
   */
  async getApiKeysByOrganization(organizationId: string): Promise<ApiKey[]> {
    try {
      console.log('Getting API keys by organization via API:', organizationId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/organization/${organizationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.apiKeys;
    } catch (error) {
      console.error('Error getting API keys by organization:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Bulk operations for API keys
   */
  async bulkUpdateApiKeys(updates: Array<{ id: string; data: Partial<ApiKey> }>): Promise<any> {
    try {
      console.log('Bulk updating API keys via API:', updates.length);
      
      const promises = updates.map(update => 
        this.updateApiKey(update.id, update.data)
      );
      
      const results = await Promise.allSettled(promises);
      
      const summary = {
        successful: 0,
        failed: 0,
        errors: [] as any[]
      };
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          summary.successful++;
        } else {
          summary.failed++;
          summary.errors.push({
            id: updates[index].id,
            error: result.reason
          });
        }
      });
      
      return summary;
    } catch (error) {
      console.error('Error bulk updating API keys:', error);
      throw error;
    }
  }

  /**
   * Export API keys data
   */
  async exportApiKeys(format: 'json' | 'csv' = 'json'): Promise<string> {
    try {
      console.log('Exporting API keys via API:', format);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/api-keys/export?format=${format}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (format === 'json') {
        return JSON.stringify(response.data.apiKeys, null, 2);
      } else {
        return response.data.exportData;
      }
    } catch (error) {
      console.error('Error exporting API keys:', error);
      throw error;
    }
  }
}

const apiKeysService = new ApiKeysService();
export default apiKeysService;
